import {useState} from 'react'
import * as Yup from 'yup'
import * as component from '../../../components/redux/componentRedux'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {changePassword, resetPassword, revokeRefreshToken} from '../apis'
import {Alerts} from '../../../components'
import {useDispatch} from 'react-redux'
import {messageError, useLocalStorage} from '../../../utils'
import {getRefreshToken, removeDataLogin} from '../helpers'

type Props = {
  userId: string
  email: string
  isEmailReset?: boolean
  isRegister?: boolean
  handleChangePasswordSubmit?: (type: string) => void
}

const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/

const initialValues = {
  currentPass: '',
  newPass: '',
  reNewPass: '',
}

const forgotPasswordSchema = Yup.object().shape({
  newPass: Yup.string()
    .matches(
      /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})/,
      'Mật khẩu yêu cầu ít nhất 8 ký tự trong đó có ít nhất 1 chữ in hoa, 1 chữ thường, 1 chữ số và 1 ký tự đặc biệt.'
    )
    .required('Mật khẩu mới bắt buộc'),
  reNewPass: Yup.string()
    .oneOf([Yup.ref('newPass'), null], 'Mật khẩu mới không đúng')
    .required('Nhập lại mật khẩu mới'),
})

const FormChangePassword = ({
  handleChangePasswordSubmit,
  userId,
  email,
  isEmailReset,
  isRegister,
}: Props) => {
  const [userRoot] = useLocalStorage<any>('persist:root', [])
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const dispatch = useDispatch()
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setTimeout(() => {
        let data: any = {
          model: {
            userId: userId,
            newPassword: values.newPass,
            confirmPassword: values.reNewPass,
          },
        }
        if (email) {
          data.model.email = email
          data.model.resetName = 'registration'
        }
        if (!isEmailReset && !values.currentPass) {
          dispatch(
            component.actions.writeAlert({
              show: true,
              type: 'warning',
              message: 'Vui lòng nhập đủ thông tin!',
            })
          )
          return
        }
        if (values.currentPass) {
          data.model.oldPassword = values.currentPass
        }
        if (isEmailReset) {
          resetPassword(data)
            .then((res: any) => {
              if (res && !res.isError) {
                if (
                  res.data == 'GoogleAuthenticator' ||
                  res.data == '1' ||
                  res.data == '2' ||
                  res.data == 'PinCode'
                ) {
                  if (handleChangePasswordSubmit) handleChangePasswordSubmit(res.data)
                } else {
                  window.location.href = '/auth/login'
                }
              }
            })
            .catch((error) => {
              dispatch(
                component.actions.writeAlert({
                  show: true,
                  type: 'error',
                  message: messageError(error.response),
                })
              )
            })
          return
        }
        data.model.sessionId = userRoot?.sessionId?.replaceAll('"', '')
        if (isRegister) data.model.resetName = 'registration'
        changePassword(data)
          .then((res: any) => {
            if (res && !res.isError) {
              if (
                (res.data == 'GoogleAuthenticator' ||
                  res.data == '1' ||
                  res.data == '2' ||
                  res.data == 'PinCode') &&
                isRegister
              ) {
                if (handleChangePasswordSubmit) handleChangePasswordSubmit(res.data)
              } else {
                dispatch(
                  component.actions.writeAlert({
                    show: true,
                    type: 'success',
                    message:
                      'Đổi mật khẩu thành công. Bạn sẽ quay trở lại trang đăng nhập sau vài giây.',
                  })
                )
                let data = {
                  refreshToken: getRefreshToken(),
                  userId: userRoot?.userId?.replaceAll('"', ''),
                }
                revokeRefreshToken(data)
                setTimeout(() => {
                  removeDataLogin()
                  window.location.href = '/auth/login'
                }, 3000)
              }
            }
          })
          .catch((error) => {
            dispatch(
              component.actions.writeAlert({
                show: true,
                type: 'error',
                message: messageError(error.response),
              })
            )
          })
      }, 500)
    },
  })

  return (
    <>
      <Alerts />
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        {hasErrors === true && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>
              Sai thông tin.
            </div>
          </div>
        )}

        {hasErrors === false && (
          <div className='mb-10 bg-light-info p-8 rounded'>
            <div className='text-info'>Mật khẩu mới đã được gửi. Kiểm tra email.</div>
          </div>
        )}
        {!isEmailReset && (
          <div className='fv-row mb-3'>
            <label className='form-label fw-bolder text-gray-900 fs-6'>Mật khẩu hiện tại</label>
            <input
              type='password'
              placeholder=''
              autoComplete='off'
              maxLength={25}
              {...formik.getFieldProps('currentPass')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {'is-invalid': formik.touched.currentPass && formik.errors.currentPass},
                {
                  'is-valid': formik.touched.currentPass && !formik.errors.currentPass,
                }
              )}
            />
            {formik.touched.currentPass && formik.errors.currentPass && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert' style={{color: 'red'}}>
                    {formik.errors.currentPass}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        <div className='fv-row mb-3'>
          <label className='form-label fw-bolder text-gray-900 fs-6'>Mật khẩu mới</label>
          <input
            type='password'
            placeholder=''
            autoComplete='off'
            maxLength={25}
            {...formik.getFieldProps('newPass')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.newPass && formik.errors.newPass},
              {
                'is-valid': formik.touched.newPass && !formik.errors.newPass,
              }
            )}
          />
          {formik.touched.newPass && formik.errors.newPass && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert' style={{color: 'red'}}>
                  {formik.errors.newPass}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-gray-900 fs-6'>Nhập lại mật khẩu</label>
          <input
            type='password'
            placeholder=''
            autoComplete='off'
            maxLength={25}
            {...formik.getFieldProps('reNewPass')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.reNewPass && formik.errors.reNewPass},
              {
                'is-valid': formik.touched.reNewPass && !formik.errors.reNewPass,
              }
            )}
          />
          {formik.touched.reNewPass && formik.errors.reNewPass && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert' style={{color: 'red'}}>
                  {formik.errors.reNewPass}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <button
            type='submit'
            id='kt_password_reset_submit'
            className='btn btn-lg btn-primary fw-bolder me-4'
          >
            <span className='indicator-label'>Đổi mật khẩu</span>
            {loading && (
              <span className='indicator-progress'>
                Vui lòng đợi...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <Link to='/'>
            <button
              type='button'
              id='kt_login_password_reset_form_cancel_button'
              className='btn btn-lg btn-light-primary fw-bolder'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              Huỷ
            </button>
          </Link>
        </div>
      </form>
    </>
  )
}

export {FormChangePassword}
